// features/auth/schemas.ts

import { z } from 'zod';
import type { TFunction } from 'i18next';

export const createSignInSchema = (t: TFunction) =>
  z
    .object({
      email: z.email({ error: t('auth:errors.email_invalid') }),
      password: z.string().min(1, t('auth:errors.password_required')),
      remember: z.boolean().default(false),
    })
    .transform((data) => ({
      ...data,
      remember: data.remember ?? false,
    }));

export const createSignUpSchema = (t: TFunction) =>
  z
    .object({
      name: z.string().min(2, t('auth:errors.name_min', { min: 2 })),
      email: z.email({ error: t('auth:errors.email_invalid') }),
      password: z
        .string()
        .min(8, t('auth:errors.password_min', { min: 8 }))
        .check(z.regex(/[A-Z]/, { error: t('auth:errors.password_uppercase') }))
        .check(z.regex(/[0-9]/, { error: t('auth:errors.password_number') })),
      confirmPassword: z.string().min(1, t('auth:errors.confirm_required')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth:errors.confirm_match'),
      path: ['confirmPassword'],
    });

export const createForgotPasswordSchema = (t: TFunction) =>
  z.object({
    email: z.email({ error: t('auth:errors.email_invalid') }),
  });

export const createVerifyRecoveryCodeSchema = (t: TFunction) =>
  z.object({
    code: z.string().regex(/^\d{6}$/, t('auth:errors.code_invalid')),
  });

export const createResetPasswordSchema = (t: TFunction) =>
  z
    .object({
      password: z
        .string()
        .min(8, t('auth:errors.password_min', { min: 8 }))
        .check(z.regex(/[A-Z]/, { error: t('auth:errors.password_uppercase') }))
        .check(z.regex(/[0-9]/, { error: t('auth:errors.password_number') })),
      confirmPassword: z.string().min(1, t('auth:errors.confirm_required')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth:errors.confirm_match'),
      path: ['confirmPassword'],
    });

export type SignInFormValues = z.input<ReturnType<typeof createSignInSchema>>;
export type SignUpFormValues = z.infer<ReturnType<typeof createSignUpSchema>>;
export type ForgotPasswordFormValues = z.infer<ReturnType<typeof createForgotPasswordSchema>>;
export type VerifyRecoveryCodeFormValues = z.infer<
  ReturnType<typeof createVerifyRecoveryCodeSchema>
>;
export type ResetPasswordFormValues = z.infer<ReturnType<typeof createResetPasswordSchema>>;
